import Vue from 'vue';
import { isEqual } from 'lodash';
import { VIEW_TYPES } from '~/assets/javascript/constants';
import { extractViewRecords } from '~/assets/javascript/utils';

export const state = () => ({
  view: {},
  locale: 'en',
  subviews: [],
  viewsChangedAtMapping: {},
});

export const cacheableStateKeys = [
  'locale',
];

export const getters = {
  currentViewChangedAt: ({ viewsChangedAtMapping, view }) => viewsChangedAtMapping[view.id],
  // View configs
  layoutOptions: ({ view }) => VIEW_TYPES[view.page_type]?.builderOptions || {},
  componentsOptions: ({ view }) => (VIEW_TYPES[view.page_type]?.components || []).reduce((acc, component) => {
    acc[component.key] = component;
    return acc;
  }, {}),
  // Permissions
  canEdit: ({ view }) => Boolean(view.permit_record_update),
  canCreate: ({ view }) => Boolean(view.permit_record_insert),
  canDelete: ({ view }) => Boolean(view.permit_record_delete),
  redirectOptions: ({ view }) => view.fields_redirect_options,
  viewAppData: ({ view: { metadata = {}, page_type: pageType, page_name: pageName } }) => ({
    apps: metadata.apps,
    appCategories: metadata.app_categories,
    layout: pageType,
    name: pageName,
  }),
  findSubview: ({ subviews }) => (viewId, fieldId) => subviews.find(
    subview => subview.metadata.parent_view_id === viewId
      && subview.metadata.from_field_id === fieldId,
  ),
  isArchived: ({ view }) => Boolean(view.archived_at),
  pagePublished: ({ view }) => view.page_published,
};

const loadSubviews = (state, view) => {
  (view.subviews || []).forEach((subview) => {
    state.subviews.push(subview);
    loadSubviews(state, subview);
  });
};

export const mutations = {
  clearView(state) {
    state.view = {};
    state.subviews = [];
    state.viewsChangedAtMapping = {};
  },
  setView(state, payload) {
    state.view = payload;
    state.subviews = [];
    state.viewsChangedAtMapping = {};

    loadSubviews(state, payload);
  },
  refreshTableView(state, view) {
    Object.keys(view).forEach((key) => {
      if (isEqual(state.view[key], view[key])) return;
      Vue.set(state.view, key, view[key]);
    });
  },
  setLocale(state, value) {
    state.locale = value;
  },
  setViewChangedAt(state, { viewId, changedAt }) {
    state.viewsChangedAtMapping = {
      ...state.viewsChangedAtMapping,
      [viewId]: changedAt,
    };
  },
};

export const actions = {
  async loadView({ commit, state }, { viewId, recordId, tenantSlug = null, refresh = false, isPublic = false }) {
    const params = {};

    if (recordId) {
      params.record_id = recordId;
    }

    const path = isPublic ? `/public/workspaces/${tenantSlug}/views/${viewId}` : `/views/${viewId}`;

    const { records, view } = extractViewRecords(
      await this.$api.$get(path, { params }),
    );

    // reset records if view changed
    if (!refresh) commit('records/reset', null, { root: true });
    // reset filters only if the new view is different from the current one
    // when opening a record from an index of records the view will be loaded again
    // and the filters should not be reset, because it is loaded asynchronously and can be overridden
    if (viewId !== state.view.id) commit('records/resetViewFilters', null, { root: true });

    commit('setLocale', this.$i18n.locale);
    commit('viewFields/setFields', view.fields, { root: true });
    commit('records/loadRecordsByView', { ...view, records }, { root: true });

    if (refresh) {
      const mutationName = view.page_type === 'Table' ? 'refreshTableView' : 'setView';

      if (window) window.dispatchEvent(new Event('global:beforeViewRefresh'));
      commit(mutationName, view);
      if (window) window.dispatchEvent(new Event('global:afterViewRefresh'));
    } else {
      commit('setView', view);
    }
  },
  fakeLoadBuilderView({ commit }, view) {
    return Promise.all([
      commit('setView', view),
      commit('viewFields/setFields', view.fields, { root: true }),
    ]);
  },
  markViewAsChanged({ commit }, viewId) {
    commit('setViewChangedAt', { viewId, changedAt: Date.now() });
  },
};
